import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import message from 'antd/lib/message';
import Model from 'antd/lib/modal';
import Upload from 'antd/lib/upload';
import React, { useContext, useEffect, useState } from 'react';

import { axiosPutCall } from '../../api/axiosCall';
import { API_END_POINT, SOMETHING_WENT_WRONG } from '../../constant/constants';
import { AuthContext } from '../../context/AuthProvider';
import { uploadFile, S3ImageFolder } from '../../utils/imageUploader';

function EditCategoryModel({
  isOpen,
  category,
  callback,
  handleEditModelCloseClick,
}) {
  const [editForm] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [imagePath, setImagePath] = useState(category.categoryImagePath);
  const { token } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  useEffect(() => {
    setImagePath(category.categoryImagePath);
    editForm.setFieldValue(category);
  }, [category]);

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
      setImagePath('');
    },
    beforeUpload: async (file) => {
      if (fileList.length < 1) {
        setFileList([...fileList, file]);
        // Automatically upload the image when selected
        try {
          setIsImageUploading(true);
          const uploadedImage = await uploadFile(
            file,
            S3ImageFolder.ProductCategory,
            token,
          );
          setImagePath(uploadedImage);
          setIsImageUploading(false);
          message.success('Image uploaded successfully');

          // Remove the uploaded file from the upload preview list
          const index = fileList.indexOf(file);
          const newFileList = fileList.slice();
          newFileList.splice(index, 1);
          setFileList(newFileList);
        } catch (error) {
          setIsImageUploading(false);
          console.error('Image upload error:', error);

          // Provide more specific error messages based on error type
          let errorMessage = 'Failed to upload image';
          if (error.response) {
            // Server responded with error status
            if (error.response.status === 413) {
              errorMessage =
                'Image file is too large. Please select a smaller image.';
            } else if (error.response.status === 415) {
              errorMessage =
                'Invalid image format. Please select a valid image file.';
            } else if (error.response.status >= 500) {
              errorMessage =
                'Server error occurred while uploading image. Please try again.';
            } else if (error.response.data && error.response.data.message) {
              errorMessage = error.response.data.message;
            }
          } else if (error.request) {
            // Network error
            errorMessage =
              'Network error. Please check your internet connection and try again.';
          } else if (error.message) {
            // Other error
            errorMessage = `Upload failed: ${error.message}`;
          }

          message.error(errorMessage);

          // Remove the file from fileList if upload fails
          const index = fileList.indexOf(file);
          const newFileList = fileList.slice();
          newFileList.splice(index, 1);
          setFileList(newFileList);
        }
      } else {
        message.error('Max image upload limit reached');
      }
      return false;
    },
    fileList,
  };

  async function editProductCategory({ fileURL }) {
    try {
      if (!fileURL && !imagePath) {
        message.error('Please upload an image');
        return;
      }
      if (!editForm.getFieldValue().categoryName) {
        message.error('Please enter a category name');
        return;
      }
      const res = await axiosPutCall({
        apiEndPoint: API_END_POINT.UPDATE_PRODUCT_CATEGORY,
        header: {
          Authorization: `Bearer ${token}`,
        },
        body: {
          categoryName: editForm.getFieldValue().categoryName,
          categoryImagePath: fileURL,
          productCategoryId: category.productCategoryId,
          updatedBy: 'string',
          updatedDate: new Date().toISOString(),
        },
      });
      if (res?.hideErrorFromUser) {
        message.success(res.data.message);
        callback();
        handleEditModelCloseClick();
        setIsLoading(false);
        return;
      } else {
        message.error(res?.error?.message);
        setIsLoading(false);
      }
    } catch (error) {
      message.error(SOMETHING_WENT_WRONG);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }

  async function updateProductCategory() {
    try {
      setIsLoading(true);
      await editProductCategory({ fileURL: imagePath });
    } catch (error) {
      console.error('updateProductCategory', error);
      setIsLoading(false);
      message.error(SOMETHING_WENT_WRONG);
    }
  }
  return (
    <div>
      <Model
        open={isOpen}
        onCancel={handleEditModelCloseClick}
        closable={false}
        footer={null}
      >
        <div className='flex flex-col space-y-4'>
          <div className='font-bold text-2xl'>Edit Category</div>
          <Form
            form={editForm}
            initialValues={category}
          >
            <Form.Item
              label='Category Name'
              name='categoryName'
            >
              <Input name='categoryName' />
            </Form.Item>
            <div className='flex flex-row space-x-3'>
              {imagePath ? (
                <div className='w-24 h-24 flex flex-col justify-between items-center'>
                  <img
                    src={imagePath}
                    alt={`image_${category.productCategoryId}`}
                    className=' w-20 h-20 object-contain border border-black'
                  />
                  <DeleteOutlined
                    className='pt-2'
                    onClick={() => setImagePath('')}
                  />
                </div>
              ) : (
                <Form.Item
                  label='Upload'
                  valuePropName='fileList'
                  getValueFromEvent={normFile}
                >
                  <Upload
                    accept='image/*'
                    {...uploadProps}
                    multiple={false}
                    maxCount={1}
                    disabled={isImageUploading}
                  >
                    <Button
                      type='dashed'
                      disabled={isImageUploading}
                      icon={<UploadOutlined />}
                      className='border-primary text-primary'
                    >
                      {isImageUploading ? 'Uploading...' : 'Click to Upload'}
                    </Button>
                  </Upload>
                </Form.Item>
              )}
            </div>
          </Form>

          <div className='flex flex-row justify-around'>
            <Button
              onClick={handleEditModelCloseClick}
              className='w-28 bg-surface border border-border text-text hover:text-primary hover:border-primary'
            >
              Close
            </Button>
            <Button
              onClick={updateProductCategory}
              disabled={isLoading || isImageUploading}
              type='primary'
              className='w-32 border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md'
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </Model>
    </div>
  );
}

export default EditCategoryModel;
